/**
 * Which cached queries carry a member's ROW — the name, the look (avatarId / avatarConfig) and
 * the futures chips (winner + top scorer) that every board draws beside their points.
 *
 * One predicate rather than a per-caller key list, because the rows are the same rows wherever
 * they appear: change your winner pick or your avatar, and every one of these goes stale at
 * once. Key factories live in `src/worldcup/constants/worldcupQueryKeys.ts`:
 *
 *   ['worldcup', cid, 'leaderboard', …]                          global board
 *   ['worldcup', cid, 'groups']                                  squad list (member face stack)
 *   ['worldcup', cid, 'groups', gid, 'leaderboard', …]            squad board (+ 'infinite' / 'tv')
 *   ['worldcup', cid, 'groups', gid, 'members', mid, 'profile']   member profile
 *   ['worldcup', cid, 'participants', mid, 'profile']             participant profile
 *
 * Matched by key SHAPE, so every competition — World Cup, Champions League, a season pool — is
 * covered by one rule. These queries sit behind a 3-minute `staleTime` with
 * `refetchOnWindowFocus: false`, so a writer that does not invalidate them leaves the old value
 * on screen until the window expires or the user pulls to refresh. That is exactly what a saved
 * futures pick did: it re-read the picks screen and nothing else, so the squad standings table
 * kept showing the previous winner.
 */

/**
 * Key segments that mark a payload as carrying member rows.
 *
 * `'groups'` is here for the squad LIST, whose cards draw the member face stack. It also
 * matches `worldCupQueryKeys.group(cid, gid)` by shape — inert, because no query is registered
 * under that factory — so the only live key it adds is the list itself.
 */
const MEMBER_ROW_SEGMENTS = new Set(['leaderboard', 'groups', 'members', 'participants']);

export function isMemberRowQueryKey(queryKey: readonly unknown[]): boolean {
  if (queryKey[0] !== 'worldcup') return false;
  // Competition-scoped keys put the discriminator at index 2; squad-scoped keys push it to 4.
  return (
    MEMBER_ROW_SEGMENTS.has(queryKey[2] as string) ||
    MEMBER_ROW_SEGMENTS.has(queryKey[4] as string)
  );
}
